import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import { computeMonthlyCostMetrics, computeNon2AdlDaysThisRow } from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  compoundFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  getPhaseCareMonthsByPhase,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';

// ---------------------------------------------------------------------------
// OneAmerica AnnuityCare II — single-premium deferred annuity with a built-in
// LTC Accumulated Value (LTCAV) and a separately-priced Continuation of
// Benefits (COB) rider (calculationType `annuityHybrid`, single-life only —
// no joint variant). See docs/oa-annuity-care-two/field-mapping.md.
//
// TWO REAL POOLS, SEQUENTIAL DRAWDOWN — unlike the single-pool ForeCare /
// EquiTrust Bridge siblings, AnnuityCare II is a genuine two-pool product
// (Total LTC Balance = LTCAV + COB). Structurally this mirrors Asset Care's
// AOB-then-COB drawdown, with three product-specific twists:
//
//  - LTCAV (base pool) is the FIRST pool drawn. Its monthly cap = scheduled
//    LTCAV / its benefit period, where the period is read per-policy from
//    `policyAccelerationBenefitDurationMonths` (24 months on the sample
//    illustration, but variable — see the FALLBACK constants below). New
//    uploads populate generic rate-model year-1 values and guaranteed-rate
//    fields, so the strategy can derive the scheduled LTCAV for each policy
//    year. Rows with `policyBaseBenefitAmountAtIssue` but an incomplete rate
//    field set keep the flat year-1 `policyMaximumBenefitAmount`
//    interpretation (cap = LTCAV / period).
//  - PRE-RATE-MODEL ROWS (`policyBaseBenefitAmountAtIssue` null) are NEVER
//    reinterpreted. The old extraction prompt stored the age-specific Total
//    LTC Balance in `policyMaximumBenefitAmount` and the age-specific LTC
//    Monthly Benefit in the daily fields, so deriving a cap as pool / period
//    would inflate it ~4× (the field-mapping doc explicitly forbids this
//    silent reinterpretation). These rows stay on the legacy interpretation —
//    pool dollars as stored, monthly cap from the daily-benefit fields — and
//    `buildClaimTerms` raises a `dataWarnings` entry asking for a re-upload
//    (data-versioning Option A: stale-data detection + warning).
//  - COB (eob pool) becomes available ONLY once the LTCAV reaches zero.
//    Its monthly cap = scheduled COB / its benefit period, read per-policy from
//    `policyEobBenefitDurationMonths` (72 months on the sample illustration).
//    `policyContinuationBenefitAmount` holds the year-1 COB Balance. The COB
//    pool AND its monthly cap inflate 5% compound, anchored at issue, applied
//    each anniversary from year 2, with NO cap year
//    (`policyInflationProtectionOnCOBAmount`). At issue the two monthly caps
//    are equal (LTCAV/24 == COB/72 == $2,336.38 on the sample); they diverge
//    thereafter because only COB inflates.
//  - DEATH BENEFIT / AV FLOOR — the deferred-annuity Accumulated Value
//    ($56,046 yr1) is "reduced on a proportional basis" by LTCAV draws (p.3):
//    each row closes the AV at open × (LTCAV after draw / LTCAV before draw),
//    so the AV falls by the same percentage as the LTCAV pool and reaches 0
//    exactly when the pool exhausts (P2-30). LTCAV draws ONLY — the COB pool
//    is independent of the AV (COB draws do NOT reduce it).
//    `finalizeDeathBenefit` returns the residual non-LTC payout.
//
// Both monthly caps derive from the scheduled pool size / benefit period rather
// than the daily-benefit field, because the carrier ships two distinct
// period-derived limits (LTCAV/24, COB/72) that a single daily field cannot
// represent.
//
// PAYOUT MODEL — reimbursement (default; per the illustration) or cash
// indemnity (honored via `isIndemnityPolicyPayment`). Standard 2-ADL gate then
// a 90-day elimination period; EP days are strictly out-of-pocket, no
// retroactive lump.
//
// The two pools + the deathBenefit slot capture everything path-dependent, so
// both strategy slots are empty (mirroring Asset Care single-life).
// ---------------------------------------------------------------------------

export interface AnnuityCareTwoState {
  // Indemnity-surplus reserve. Indemnity policies pay a fixed monthly benefit
  // regardless of cost; the excess above eligible cost is banked here and
  // drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there.
  indemnitySurplus: number;
}
export interface AnnuityCareTwoDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

// FALLBACK benefit periods only. The actual periods are policy-specific and are
// read from `policyAccelerationBenefitDurationMonths` (LTCAV) and
// `policyEobBenefitDurationMonths` (COB) — see `readPoolConfig`. These constants
// are used solely when those fields are missing/non-positive (e.g. legacy rows
// extracted before the duration fields existed). 24/72 are the OneAmerica
// AnnuityCare II product defaults ("usually 24/72" per the field-mapping doc),
// NOT a fixed assumption applied to every policy.
const DEFAULT_LTCAV_BENEFIT_PERIOD_MONTHS = 24;
const DEFAULT_COB_BENEFIT_PERIOD_MONTHS = 72;

// Surfaced on `terms.dataWarnings` for pre-rate-model rows (P2-29): the row is
// kept on its legacy interpretation rather than silently reinterpreted, and the
// user is asked to refresh the extraction.
export const ANNUITY_CARE_TWO_STALE_EXTRACTION_WARNING =
  'This AnnuityCare II policy was extracted before the guaranteed-rate model existed, so its ' +
  'stored benefit fields use the older single-pool interpretation. Re-upload the illustration ' +
  'to refresh the extracted fields and enable the full LTCAV + COB projection.';

interface PoolConfig {
  usesGuaranteedRateModel: boolean;
  // True when the row predates the rate-model extraction entirely
  // (`policyBaseBenefitAmountAtIssue` null). Such rows keep the legacy
  // interpretation: `policyMaximumBenefitAmount` is the stored pool dollar
  // (NOT a year-1 LTCAV to divide by the benefit period) and the monthly cap
  // comes from the daily-benefit fields. See P2-29 in the header comment.
  isPreRateModelRow: boolean;
  // Year-1 at-issue pool sizes.
  ltcavAtIssue: number;
  cobAtIssue: number;
  ltcavBenefitPeriodMonths: number;
  cobBenefitPeriodMonths: number;
  ltcavInitialRate: number;
  ltcavInitialRateYears: number;
  ltcavRenewalRate: number;
  monthlyCobChargeRate: number;
  cobRate: number;
  cobFirstAnniversaryRate: number;
  cobCapYears: number;
  hasAvRateModel: boolean;
  avAtIssue: number;
  avInitialRate: number;
  avInitialRateYears: number;
  avRenewalRate: number;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function shouldUseAnnuityCareTwoGuaranteedRateModel(
  ltcPolicy: PolicyFundingSource,
): boolean {
  return (
    isFiniteNumber(ltcPolicy.policyBaseBenefitAmountAtIssue) &&
    isFiniteNumber(ltcPolicy.policyContinuationBenefitAmountAtIssue) &&
    isFiniteNumber(ltcPolicy.policyBaseBenefitGuaranteedInitialRate) &&
    isFiniteNumber(ltcPolicy.policyBaseBenefitGuaranteedInitialRateYears) &&
    isFiniteNumber(ltcPolicy.policyBaseBenefitGuaranteedRenewalRate) &&
    isFiniteNumber(ltcPolicy.policyMonthlyContinuationBenefitChargeRate) &&
    isFiniteNumber(ltcPolicy.policyContinuationBenefitFirstAnniversaryRate)
  );
}

function shouldUseAvRateModel(ltcPolicy: PolicyFundingSource): boolean {
  return (
    isFiniteNumber(ltcPolicy.policyAccumulatedValueAtIssue) &&
    isFiniteNumber(ltcPolicy.policyAccumulatedValueGuaranteedInitialRate) &&
    isFiniteNumber(ltcPolicy.policyAccumulatedValueGuaranteedInitialRateYears) &&
    isFiniteNumber(ltcPolicy.policyAccumulatedValueGuaranteedRenewalRate)
  );
}

function readPositiveMonths(value: number | null | undefined, fallback: number): number {
  return isFiniteNumber(value) && value > 0 ? value : fallback;
}

function readPoolConfig(ltcPolicy: PolicyFundingSource): PoolConfig {
  const usesGuaranteedRateModel = shouldUseAnnuityCareTwoGuaranteedRateModel(ltcPolicy);
  const hasAvRateModel = usesGuaranteedRateModel && shouldUseAvRateModel(ltcPolicy);
  const isPreRateModelRow = !isFiniteNumber(ltcPolicy.policyBaseBenefitAmountAtIssue);
  const ltcavAtIssue = usesGuaranteedRateModel
    ? ltcPolicy.policyBaseBenefitAmountAtIssue!
    : (ltcPolicy.policyMaximumBenefitAmount ?? 0);
  const cobAtIssue = usesGuaranteedRateModel
    ? ltcPolicy.policyContinuationBenefitAmountAtIssue!
    : (ltcPolicy.policyContinuationBenefitAmount ?? 0);
  // COB inflation reads the COB-specific knob; fall back to the policy-wide
  // rate (some uploads carry only `policyInflationProtection`). The LTCAV is
  // scheduled from guaranteed rates only when the new field set is complete.
  const cobRate =
    ltcPolicy.policyInflationProtectionOnCOBAmount ?? ltcPolicy.policyInflationProtection ?? 0;
  // No 20-year cap on the AnnuityCare II COB inflation (compounds through the
  // whole illustration). Honor an explicit cap if a future upload supplies one.
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    usesGuaranteedRateModel,
    isPreRateModelRow,
    ltcavAtIssue,
    cobAtIssue,
    // Benefit periods are policy-specific: read from the extracted duration
    // fields, falling back to the product defaults only when absent.
    ltcavBenefitPeriodMonths: readPositiveMonths(
      ltcPolicy.policyAccelerationBenefitDurationMonths,
      DEFAULT_LTCAV_BENEFIT_PERIOD_MONTHS,
    ),
    cobBenefitPeriodMonths: readPositiveMonths(
      ltcPolicy.policyEobBenefitDurationMonths,
      DEFAULT_COB_BENEFIT_PERIOD_MONTHS,
    ),
    ltcavInitialRate: usesGuaranteedRateModel
      ? ltcPolicy.policyBaseBenefitGuaranteedInitialRate!
      : 0,
    ltcavInitialRateYears: usesGuaranteedRateModel
      ? ltcPolicy.policyBaseBenefitGuaranteedInitialRateYears!
      : 0,
    ltcavRenewalRate: usesGuaranteedRateModel
      ? ltcPolicy.policyBaseBenefitGuaranteedRenewalRate!
      : 0,
    monthlyCobChargeRate: usesGuaranteedRateModel
      ? ltcPolicy.policyMonthlyContinuationBenefitChargeRate!
      : 0,
    cobRate,
    cobFirstAnniversaryRate: usesGuaranteedRateModel
      ? ltcPolicy.policyContinuationBenefitFirstAnniversaryRate!
      : cobRate,
    cobCapYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    hasAvRateModel,
    avAtIssue: hasAvRateModel
      ? ltcPolicy.policyAccumulatedValueAtIssue!
      : (ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0),
    avInitialRate: hasAvRateModel ? ltcPolicy.policyAccumulatedValueGuaranteedInitialRate! : 0,
    avInitialRateYears: hasAvRateModel
      ? ltcPolicy.policyAccumulatedValueGuaranteedInitialRateYears!
      : 0,
    avRenewalRate: hasAvRateModel ? ltcPolicy.policyAccumulatedValueGuaranteedRenewalRate! : 0,
  };
}

function rateForContractYear(
  initialRate: number,
  initialRateYears: number,
  renewalRate: number,
  contractYear: number,
): number {
  return contractYear <= initialRateYears ? initialRate : renewalRate;
}

function monthlyEffectiveRate(annualRate: number): number {
  return Math.pow(1 + annualRate, 1 / 12) - 1;
}

function normalizedPolicyYear(policyYear: number): number {
  return Math.max(Math.floor(policyYear), 1);
}

function scheduledAnnuityValues(
  config: PoolConfig,
  policyYear: number,
): { ltcav: number; av: number } {
  const targetYear = normalizedPolicyYear(policyYear);
  let ltcav = config.ltcavAtIssue;
  let av = config.avAtIssue;

  if (!config.usesGuaranteedRateModel) {
    return { ltcav, av };
  }

  for (let contractYear = 2; contractYear <= targetYear; contractYear += 1) {
    const ltcavMonthlyRate = monthlyEffectiveRate(
      rateForContractYear(
        config.ltcavInitialRate,
        config.ltcavInitialRateYears,
        config.ltcavRenewalRate,
        contractYear,
      ),
    );
    const avMonthlyRate = config.hasAvRateModel
      ? monthlyEffectiveRate(
          rateForContractYear(
            config.avInitialRate,
            config.avInitialRateYears,
            config.avRenewalRate,
            contractYear,
          ),
        )
      : 0;

    for (let month = 0; month < 12; month += 1) {
      if (config.hasAvRateModel && av > 0) {
        const charge = ltcav * config.monthlyCobChargeRate;
        const openAv = av;
        av = Math.max(av - charge, 0);
        ltcav *= av / openAv;
        av *= 1 + avMonthlyRate;
      } else {
        ltcav = Math.max(ltcav * (1 - config.monthlyCobChargeRate), 0);
      }
      ltcav *= 1 + ltcavMonthlyRate;
    }
  }

  return { ltcav, av };
}

function scheduledCobBalance(config: PoolConfig, policyYear: number): number {
  const targetYear = normalizedPolicyYear(policyYear);
  if (targetYear <= 1) return config.cobAtIssue;

  if (!config.usesGuaranteedRateModel) {
    return config.cobAtIssue * compoundFactor(config.cobRate, config.cobCapYears, targetYear);
  }

  const regularGrowthYearsRaw = Math.max(targetYear - 2, 0);
  const regularGrowthYears =
    config.cobCapYears > 0
      ? Math.min(regularGrowthYearsRaw, Math.max(config.cobCapYears - 1, 0))
      : regularGrowthYearsRaw;
  return (
    config.cobAtIssue *
    (1 + config.cobFirstAnniversaryRate) *
    Math.pow(1 + config.cobRate, regularGrowthYears)
  );
}

export const annuityCareTwoStrategy: PolicyProjectionStrategy<
  AnnuityCareTwoState,
  AnnuityCareTwoDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<AnnuityCareTwoState> {
    const config = readPoolConfig(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    // Anniversaries between policy issue and claim start are not iterated (the
    // projection only spans claim months), so seed both pools at their scheduled
    // value at claim start. Legacy rows still seed the old flat LTCAV value.
    // Same policy-year baseline as `buildYearSnapshot`
    // (constraints 6 & 7).
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const claimStartValues = scheduledAnnuityValues(config, claimStartPolicyYear);
    const ltcavAtClaimStart = claimStartValues.ltcav;
    const cobAtClaimStart = scheduledCobBalance(config, claimStartPolicyYear);
    return {
      pools: {
        // LTCAV — drawn first. Prior utilization draws from LTCAV before COB.
        baseBenefitPool: Math.max(ltcavAtClaimStart - utilizedToDate, 0),
        // COB — drawn only once LTCAV is exhausted.
        extensionOfBenefitsPool: Math.max(
          cobAtClaimStart - Math.max(utilizedToDate - ltcavAtClaimStart, 0),
          0,
        ),
        // Annuity Accumulated Value floor; depleted by LTCAV draws, residual
        // surfaced via finalizeDeathBenefit.
        deathBenefit: claimStartValues.av,
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const config = readPoolConfig(ltcPolicy);
    // Pre-rate-model rows keep the legacy daily-field cap (no pool / period
    // derivation — see P2-29 in the header comment).
    const ltcavMonthlyAtIssue = config.isPreRateModelRow
      ? getMaxMonthlyBenefitAmount(ltcPolicy)
      : config.ltcavAtIssue / config.ltcavBenefitPeriodMonths;
    const dailyBenefit = ltcavMonthlyAtIssue / DAYS_IN_MONTH;
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // Prior utilization deducted across both pools at claim start: LTCAV first,
    // then COB (mirrors `initClaimState`). Total deducted is capped at the sum
    // of both scheduled pools at claim start.
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const ltcavAtClaimStart = scheduledAnnuityValues(config, claimStartPolicyYear).ltcav;
    const cobAtClaimStart = scheduledCobBalance(config, claimStartPolicyYear);
    const priorUtilization = Math.min(utilizedToDate, ltcavAtClaimStart + cobAtClaimStart);
    return {
      strategyId: 'annuity-care-two',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // The per-environment daily fields are equal to the LTCAV cap at issue on
      // the sample; expose them for display parity with siblings.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.home,
        ),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // The LTCAV (base) pool is scheduled from guaranteed annuity rates when
      // the new field set is complete, not from a policy inflation rider. Keep
      // product-wide / base inflation at 0 so display code does not treat it as
      // generic rider inflation. The COB rate drives the EOB pool + COB cap.
      inflationRate: 0,
      inflationCapYears: 0,
      baseBenefitPoolInflationRate: 0,
      baseBenefitPoolInflationCapYears: 0,
      extensionOfBenefitsPoolInflationRate: config.cobRate,
      extensionOfBenefitsPoolInflationCapYears: config.cobCapYears,
      baseBenefitPoolAtIssue: config.ltcavAtIssue,
      extensionOfBenefitsPoolAtIssue: config.cobAtIssue,
      // Pre-rate-model rows suppress the LTCAV benefit period: their base cap
      // is the legacy daily-field cap, not pool / period, so surfacing 24
      // would invite a misleading "÷ N months" derivation in the UI (P2-29;
      // same convention as Asset Flex legacy rows). The COB cap is still
      // genuinely pool / period in every mode, so its duration stays.
      ...(config.isPreRateModelRow
        ? {}
        : { baseBenefitPoolDurationMonths: config.ltcavBenefitPeriodMonths }),
      extensionOfBenefitsPoolDurationMonths: config.cobBenefitPeriodMonths,
      ...(config.usesGuaranteedRateModel
        ? {
            baseBenefitGuaranteedInitialRate: config.ltcavInitialRate,
            baseBenefitGuaranteedInitialRateYears: config.ltcavInitialRateYears,
            baseBenefitGuaranteedRenewalRate: config.ltcavRenewalRate,
            monthlyContinuationBenefitChargeRate: config.monthlyCobChargeRate,
            continuationBenefitFirstAnniversaryRate: config.cobFirstAnniversaryRate,
          }
        : {}),
      ...(config.hasAvRateModel
        ? {
            accumulatedValueAtIssue: config.avAtIssue,
            accumulatedValueGuaranteedInitialRate: config.avInitialRate,
            accumulatedValueGuaranteedInitialRateYears: config.avInitialRateYears,
            accumulatedValueGuaranteedRenewalRate: config.avRenewalRate,
          }
        : {}),
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // The annuity Accumulated Value floor surfaced as the residual non-LTC
      // payout.
      minimumGuaranteedDeathBenefit: config.avAtIssue,
      isReimbursement: !isIndemnity,
      isIndemnity,
      ...(config.isPreRateModelRow
        ? { dataWarnings: [ANNUITY_CARE_TWO_STALE_EXTRACTION_WARNING] }
        : {}),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const config = readPoolConfig(ltcPolicy);
    const { ltcav, av } = scheduledAnnuityValues(config, policyYear);
    const cob = scheduledCobBalance(config, policyYear);
    // Pre-rate-model rows keep the legacy daily-field cap (P2-29).
    const ltcavMonthlyCap = config.isPreRateModelRow
      ? getMaxMonthlyBenefitAmount(ltcPolicy)
      : ltcav / config.ltcavBenefitPeriodMonths;
    const cobMonthlyCap = cob / config.cobBenefitPeriodMonths;

    return {
      policyYear,
      // The LTCAV phase governs the headline daily/monthly cap.
      effectiveDailyBenefit: ltcavMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: ltcavMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: cobMonthlyCap / DAYS_IN_MONTH,
      // No life face. The LTCAV pool reads through `effectiveFaceAmount` so the
      // two-pool UI can render both pools (Asset Care convention: base pool ↔
      // face slot, eob pool ↔ continuation slot).
      effectiveFaceAmount: ltcav,
      effectiveContinuationBenefit: cob,
      effectiveTotalLtcBenefit: ltcav + cob,
      monthlyBenefitCap: ltcavMonthlyCap,
      continuationMonthlyBenefitCap: cobMonthlyCap,
      // Annuity AV floor on the same guaranteed schedule; the pre-care UI reads
      // this instead of re-deriving the crediting schedule client-side.
      accumulatedValue: av,
      theoreticalLtcPoolCeiling: ltcav + cob,
    };
  },

  computeMonth(
    input: MonthComputeInput<AnnuityCareTwoState, AnnuityCareTwoDeltas>,
  ): MonthlyEntry<AnnuityCareTwoState, AnnuityCareTwoDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate — a care month before the premium-start year pays $0,
    // full OOP. Single-life floors its policy year at 1, so this is defensive.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<AnnuityCareTwoState, AnnuityCareTwoDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-pool monthly dollar caps for this policy year. The LTCAV cap
    //    follows the guaranteed LTCAV schedule when present; flat-fallback rows
    //    keep the old flat LTCAV / period cap; pre-rate-model rows use the
    //    legacy per-environment daily-field cap (P2-29 — their stored pool
    //    dollar is NOT a year-1 LTCAV, so pool / period would inflate the cap).
    //    The COB cap follows the COB schedule.
    const config = readPoolConfig(ltcPolicy);
    const scheduledValues = scheduledAnnuityValues(config, policyYear);
    const scheduledCob = scheduledCobBalance(config, policyYear);
    const ltcavMonthlyCap = config.isPreRateModelRow
      ? getMonthlyBenefitAmountForEnvironment(ltcPolicy, phaseCarePlan.appliedCareEnvironment)
      : scheduledValues.ltcav / config.ltcavBenefitPeriodMonths;
    const cobMonthlyCap = scheduledCob / config.cobBenefitPeriodMonths;

    // 3. Annual anniversary accrual. New rate-model rows step the LTCAV against
    //    the guaranteed schedule; legacy rows keep the old flat LTCAV. COB
    //    accrues against the carrier COB schedule in either mode.
    let ltcavAccrual = 0;
    let cobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      if (config.usesGuaranteedRateModel && openState.pools.baseBenefitPool > 0) {
        const priorScheduledValues = scheduledAnnuityValues(config, priorYear);
        ltcavAccrual = scheduledValues.ltcav - priorScheduledValues.ltcav;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        cobInflationAccrual =
          scheduledCobBalance(config, policyYear) - scheduledCobBalance(config, priorYear);
      }
    }
    const ltcavAfterAccrual = openState.pools.baseBenefitPool + ltcavAccrual;
    const cobAfterAccrual = openState.pools.extensionOfBenefitsPool + cobInflationAccrual;

    // 4. Payout: LTCAV first, then COB (sequential — COB engages only once the
    //    LTCAV reaches zero). Branches on indemnity vs. reimbursement.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // `??` passes an explicit 0 through, and the day-funding math divides by
    // this — 0/0 = NaN would propagate into the row payout (P2-35). Treat any
    // non-positive percent as the full benefit.
    const indemnityPercentRaw = ltcPolicy.policyIndemnityBenefitPercent;
    const indemnityPercent =
      isFiniteNumber(indemnityPercentRaw) && indemnityPercentRaw > 0 ? indemnityPercentRaw : 1;

    let drawFromLtcav = 0;
    let drawFromCob = 0;
    let indemnityPaid = 0;
    if (isIndemnity && eligibleDays > 0) {
      // Cash indemnity: pay the pro-rated cap regardless of cost, LTCAV first
      // (at the LTCAV cap) then COB (at the COB cap). Drawdown cannot exceed
      // the pool.
      const ltcavProRated = ltcavMonthlyCap * (eligibleDays / DAYS_IN_MONTH) * indemnityPercent;
      drawFromLtcav = Math.min(ltcavProRated, ltcavAfterAccrual);
      const ltcavDaysFunded =
        ltcavMonthlyCap > 0
          ? (drawFromLtcav / indemnityPercent / ltcavMonthlyCap) * DAYS_IN_MONTH
          : 0;
      const cobEligibleDays = Math.max(eligibleDays - ltcavDaysFunded, 0);
      const cobProRated = cobMonthlyCap * (cobEligibleDays / DAYS_IN_MONTH) * indemnityPercent;
      drawFromCob = Math.min(cobProRated, cobAfterAccrual);
      indemnityPaid = drawFromLtcav + drawFromCob;
    } else if (eligibleDays > 0) {
      // Reimbursement: reimburse each eligible day up to that pool's daily cap,
      // LTCAV first then COB. LTCAV covers as many eligible days as its pool
      // allows; the remaining eligible days fall to COB (at the COB cap).
      const dailyCost = costMetrics.dailyCareCost;
      const ltcavDailyCap = ltcavMonthlyCap / DAYS_IN_MONTH;
      const cobDailyCap = cobMonthlyCap / DAYS_IN_MONTH;

      const ltcavPerDay = Math.min(dailyCost, ltcavDailyCap);
      const ltcavDaysFunded =
        ltcavPerDay > 0 ? Math.min(eligibleDays, ltcavAfterAccrual / ltcavPerDay) : 0;
      drawFromLtcav = ltcavPerDay * ltcavDaysFunded;

      const cobPerDay = Math.min(dailyCost, cobDailyCap);
      const cobDaysFunded =
        cobPerDay > 0 ? Math.min(eligibleDays - ltcavDaysFunded, cobAfterAccrual / cobPerDay) : 0;
      drawFromCob = cobPerDay * cobDaysFunded;
    }
    // Gross cash this row pays out across both pools. On indemnity this is the
    // capped cash regardless of cost; on reimbursement it is the reimbursed
    // amount (≤ eligible cost). The pools deplete by this gross cash; the
    // surplus model is a separate accounting reserve layered on top.
    const rowPayout = drawFromLtcav + drawFromCob;

    // 4b. Indemnity-surplus accounting (per-row, unconditional). Cost-coverage
    //     is capped at eligible cost; cash above eligible cost banks as surplus
    //     and drains on later shortfall rows. On reimbursement-election
    //     policies the open surplus is always 0 and the reimbursed payout never
    //     exceeds eligible cost, so this is a numeric no-op. `eligibleCost` and
    //     `rowPayout` are finite, so the surplus call is safe.
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Two pools (LTCAV + COB): exhausted only when BOTH are spent. The 1e-6
      // epsilon absorbs sub-cent IEEE-754 dust from day-funding draws (which use
      // plain subtraction, not an exact `min`), matching cmTwo's threshold.
      policyExhausted: ltcavAfterAccrual + cobAfterAccrual <= 1e-6,
    });

    // 5. Closing balances. Only LTCAV drawdown depletes the AV floor (the COB
    //    pool is independent of the AV). Carrier p.3: "the AV is reduced on a
    //    proportional basis" by LTCAV draws — the AV falls by the same
    //    percentage as the LTCAV pool, not dollar-for-dollar (P2-30). The AV
    //    therefore reaches 0 exactly when the LTCAV pool exhausts.
    //    Pre-rate-model rows are exempt: their base pool holds the old
    //    prompt's Total LTC Balance, NOT the LTCAV, so a ratio against it
    //    would understate AV depletion (a third model neither the carrier nor
    //    legacy produced). They keep the legacy dollar-for-dollar clamp —
    //    same no-reinterpretation rule as the cap (P2-29).
    const closeLtcav = ltcavAfterAccrual - drawFromLtcav;
    const closeCob = cobAfterAccrual - drawFromCob;
    const closeDeathBenefit = config.isPreRateModelRow
      ? Math.max(openState.pools.deathBenefit - drawFromLtcav, 0)
      : ltcavAfterAccrual > 0
        ? Math.max(openState.pools.deathBenefit * (closeLtcav / ltcavAfterAccrual), 0)
        : openState.pools.deathBenefit;
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    // Flag the row where proportional depletion takes the AV residual to 0
    // (the LTCAV-exhausting row). Gated on a positive opening floor so
    // post-exhaustion rows (floor already 0) don't re-flag.
    const deathBenefitFloored = openState.pools.deathBenefit > 0 && closeDeathBenefit === 0;

    // The applied cap follows the pool that actually funded the row: COB once
    // it draws, or once the LTCAV was already empty at open (a pure COB-phase
    // row). The LTCAV-exhausting row still funded its payout from the LTCAV, so
    // it keeps the LTCAV cap even though it closes the LTCAV at 0.
    const inCobPhase = drawFromCob > 0 || (ltcavAfterAccrual <= 0 && drawFromLtcav === 0);

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inCobPhase ? cobMonthlyCap : ltcavMonthlyCap,
      appliedCapPoolSource: inCobPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<AnnuityCareTwoDeltas> = {
      payout,
      base: { depletion: drawFromLtcav, inflationAccrual: ltcavAccrual },
      eob: { depletion: drawFromCob, inflationAccrual: cobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const closeState: MonthlyState<AnnuityCareTwoState> = {
      pools: {
        baseBenefitPool: closeLtcav,
        extensionOfBenefitsPool: closeCob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeLtcav + closeCob <= 0,
      clientPoolExhausted: closeLtcav + closeCob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<AnnuityCareTwoState, AnnuityCareTwoDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      // Residual AV can keep drawing down to $0, so do not reuse the generic
      // minimum-death-benefit milestone when it crosses an intermediate value.
      emitMinimumDeathBenefitMilestone: false,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection:
        config.usesGuaranteedRateModel || config.cobRate > 0 || ltcavAccrual > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<AnnuityCareTwoState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<AnnuityCareTwoState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // The residual annuity Accumulated Value left after LTCAV draws — the
  // non-LTC payout.
  finalizeDeathBenefit({ closingState }: FinalizeDeathBenefitInput<AnnuityCareTwoState>): number {
    return closingState.pools.deathBenefit;
  },
};
